import { Injectable } from '@angular/core';
import { Marked, type RendererObject, type TokenizerAndRendererExtension, type Tokens } from 'marked';

export interface MarkdownRenderOptions {
  /** @deprecated No-op — les blockquotes `.markdown-body` sont décorés par kng-blockquote. */
  copyBlockquotes?: boolean;
}

/** Tags HTML custom conservés par le compilateur markdown (chat / innerHTML). */
export const MARKDOWN_ALLOWED_CUSTOM_TAGS = ['kng-ask-ia'] as const;

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function customHtmlTagExtension(tag: string): TokenizerAndRendererExtension {
  const open = `<${tag}>`;
  const close = `</${tag}>`;
  return {
    name: tag,
    level: 'inline',
    start(src) {
      const index = src.indexOf(open);
      return index < 0 ? undefined : index;
    },
    tokenizer(src) {
      if (!src.startsWith(open)) {
        return undefined;
      }
      const end = src.indexOf(close);
      if (end < 0) {
        return undefined;
      }
      return {
        type: tag,
        raw: src.slice(0, end + close.length),
        text: src.slice(open.length, end)
      };
    },
    renderer(token) {
      // Angular [innerHTML] drops custom elements and unknown data-* attrs.
      // `class` is kept; kng-ask-ia upgrades the span after insert.
      return `<span class="kng-ask-ia">${escapeHtml(String(token.text ?? ''))}</span>`;
    }
  };
}

/**
 * Service centralisé de rendu Markdown → HTML.
 *
 * Remplace les 3 instances `markdown-it` historiques (kng-rules-console,
 * kng-history, kng-memories) par une seule instance MarkedJS partagée.
 *
 * MarkedJS est déjà embarqué via `@tiptap/markdown` (peer dep) — aucun
 * coût bundle additionnel par rapport à l'éditeur.
 *
 * Profil unique : `{ gfm: true, breaks: true }`.
 * - `gfm`     : tables, autolinks, strikethrough, task lists.
 * - `breaks`  : retour à la ligne simple → `<br>` (cohérent avec l'usage
 *               messages chat / résumés mémoire).
 *
 * HTML brut autorisé par défaut (équivalent `html: true` de markdown-it),
 * compatible avec les 3 consommateurs actuels.
 * Les tags de `MARKDOWN_ALLOWED_CUSTOM_TAGS` sont reconnus en inline.
 * Ils sont réémis en `<span class="kng-ask-ia">` (sanitizer Angular),
 * puis le widget les upgrade en `<kng-ask-ia>`.
 *
 * Différences à noter par rapport à markdown-it (cf. plan migration §1.5) :
 * - Pas de `typographer` (smart quotes, tirets) — invisible UX.
 * - `linkify` GFM standard uniquement (URLs avec protocole). Si retour
 *   utilisateur sur des URLs sans `http://` non détectées, ajouter
 *   `marked-linkify-it` en correctif ciblé.
 */
@Injectable({ providedIn: 'root' })
export class MarkdownRenderService {
  private readonly marked = new Marked({
    gfm: true,
    breaks: true,
    renderer: this.createRenderer()
  });

  constructor() {
    this.marked.use({
      extensions: MARKDOWN_ALLOWED_CUSTOM_TAGS.map(tag => customHtmlTagExtension(tag))
    });
  }

  render(markdown: string | null | undefined, _options: MarkdownRenderOptions = {}): string {
    if (!markdown) return '';
    const html = this.marked.parse(markdown) as string;
    return html.replace(
      /<kng-ask-ia>([\s\S]*?)<\/kng-ask-ia>/gi,
      '<span class="kng-ask-ia">$1</span>'
    );
  }

  async renderMermaid(root: ParentNode | null | undefined): Promise<void> {
    if (!root || typeof window === 'undefined') {
      return;
    }

    const nodes = Array.from(root.querySelectorAll<HTMLElement>('.mermaid:not([data-processed="true"])'));
    if (!nodes.length) {
      return;
    }

    const mermaid = (await import('mermaid')).default;
    mermaid.initialize({ startOnLoad: false, securityLevel: 'loose' });
    await mermaid.run({ nodes, suppressErrors: true });
  }

  /** @deprecated Copie gérée par `sl-copy-button` (kng-blockquote). */
  async handleMarkdownClick(_event: Event): Promise<boolean> {
    return false;
  }

  async copyElementContent(element: HTMLElement | null | undefined): Promise<void> {
    if (!element) {
      return;
    }

    const clone = element.cloneNode(true) as HTMLElement;
    clone.querySelectorAll('sl-copy-button, [data-markdown-copy]').forEach(node => node.remove());
    const htmlContent = clone.innerHTML;
    const textContent = (clone.textContent || '').trim();

    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([htmlContent], { type: 'text/html' }),
          'text/plain': new Blob([textContent], { type: 'text/plain' })
        })
      ]);
      return;
    }

    await navigator.clipboard.writeText(textContent);
  }

  private createRenderer(): RendererObject {
    return {
      code(token: Tokens.Code): string | false {
        if ((token.lang || '').trim().toLowerCase() !== 'mermaid') {
          return false;
        }
        return `<div class="mermaid">${escapeHtml(token.text)}</div>`;
      }
    };
  }
}
